"use client";
import dynamic from "next/dynamic";

import React, { useState, useEffect, useCallback, useRef } from "react";
import Image from "next/image";
import { useRouter, usePathname } from "src/i18n/navigation";
import { useSearchParams } from "next/navigation";
import nookies from "nookies";
import axios from "axios";
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { logoSrc } from "src/utils/logo-src";
import InfoIcon from "@mui/icons-material/Info";
import { useForm } from "react-hook-form";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import {
  FileWithPreview,
  useCoupon,
  useFiles,
  useOrder,
  useCartDrawer,
} from "@contexts/index";
import { useOrderQuery, usePhotoSizes } from "src/hooks";
import { useRemoveOrderPhoto } from "src/hooks/useOrderMutations";
import RescuedUploadNotice, {
  type RescuedGroup,
} from "./components/RescuedUploadNotice";
import { useQueryClient } from "@tanstack/react-query";
import { orderQueryKey } from "src/hooks/useOrder";
import {
  CreateUserOrderDTO,
  GetOrderDto,
  GetPhotoSizeDto,
  GetUserDto,
} from "src/services/generated-api";
import { OrderStatus } from "src/enums/order";
import { PaymentOption } from "src/enums/payment-option";
import {
  authApi,
  userApi,
  orderApi,
  photosApi,
  setGuestTokenCookies,
  orderProductDetailApi,
} from "src/services/api";
import {
  addOrderProduct,
  updateOrderProductQuantity,
  removeOrderProductDetail,
  removeOrderPhoto,
} from "src/api/order.api";
import { hasAttachedPhotos } from "src/utils/order-photo-groups";
import { resolveCartQuantityChange } from "src/utils/cart-product-quantity";
import { REDIRECT_TO_PHOTO_OPTIONS } from "src/constants/redirect.constant";
import {
  FAILED_BUT_OK_TO_RESUME_STATUSES,
  TERMINAL_STATUSES,
} from "src/constants/order.constant";
import {
  cartDropReason,
  orderAgeDays,
  shouldDropRestoredCartOnFetchError,
  type CartDropReason,
} from "src/utils/order-freshness";
import { clearOrderSessionFromUrl } from "src/utils/order-session-url";
import { guestTokenFromUrl } from "src/utils/guest-token-url-guard";
import { holdUploadActivity } from "src/utils/upload-activity";
import { CouponType } from "src/enums/coupon-type";
import { MINIMUM_ORDER_CENTS } from "src/constants/minimum-order.constant";
import { DEFAULT_IMAGE_SIZE_NAME } from "src/constants/default-image-size";
import UnfinishedModal from "../unfinished-modal";
import ResumeOldOrder from "../resume-old-order";
// Shown only while an upload runs, so it is not part of the page's start-up
// JavaScript (it brings Firebase sign-in and the form validators). Firebase
// is imported inside the panel, so once it shows, the Google button still
// opens its popup straight from the click.
const UploadPrefillPanel = dynamic(
  () => import("src/components/order/upload-prefill/UploadPrefillPanel"),
  { ssr: false },
);
import { shouldShowUploadPrefillPanel } from "src/components/order/upload-prefill/should-show-prefill-panel";
import { useUpload } from "src/hooks/use-upload";
import { computeETag } from "src/utils/hashing.utils";
import ProductStore from "../product-store";
import ConnectionSpeedComponent, {
  useConnectionSpeed,
} from "@components/connection";
import { getCurrencyBasedOnDomain, getDomain } from "src/utils/domain.util";
import {
  discardReasonForPreflightStatus,
  reportCartDropped,
  reportEmptyPhotoGroup,
  reportOrderDiscarded,
  reportPhotoGroupIdentification,
  reportSuspiciousOrderCreation,
  type OrderDiscardReason,
} from "src/utils/order-loss-diagnostics";
import * as Sentry from "@sentry/nextjs";
import { abandonForOffer } from "./utils/abandon-earlier-uploads";
import pLimit from "p-limit";
import { NestJSError } from "./types";
import {
  PhotoSizeSelector,
  UploadSection,
  OrderSummary,
  UploadCompleteDialog,
} from "./components";
import CreditMinimumProgress from "@components/credit-reminder/CreditMinimumProgress";
import {
  uploadFileWithRetry,
  startUploadWithOrderPhoto,
  getUrlParameter,
} from "./utils/upload-utils";
import { pickNewOrderPhotoId } from "./utils/pick-new-order-photo";
import { probeFileHandles } from "./utils/probe-handles";
import { shouldDeleteEmptyGroup } from "./utils/empty-group-cleanup";
import {
  settleAwaitingSnapshots,
  type AwaitingSnapshot,
} from "./utils/awaiting-snapshots";
import {
  forgetLandedPick,
  notePickSending,
  notePicksPresigned,
  newPickId,
  pendingPicks,
  pickSessionKey,
  type PickTarget,
  type RestorablePicks,
} from "./utils/pending-picks";
import {
  chooseResumeGroup,
  restoredPhotoSize,
  resumeUploadDecision,
  type PendingResumeUpload,
} from "./utils/resume-upload";
import PendingPicksNotice from "./components/PendingPicksNotice";
import UploadNotes from "./components/UploadNotes";
import { UPLOAD_ERROR_ID } from "./components/UploadSection";
import { mergeNote, type UploadNote } from "./utils/upload-notes";
import { useInAppBrowser } from "src/hooks/useInAppBrowser";
import { createThumbnail } from "@components/photo-upload";
import { inAppBrowserTag } from "./utils/in-app-tag";
import { detectCurrentInAppBrowser } from "src/utils/in-app-browser";
import { stallCauseTag } from "./utils/stall-advice";
import { cartRestorePhotoSizeId, useUrlPhotoSize } from "./utils/url-photo-size";
import {
  snapshotFileBytesResult,
  snapshotTimeoutFor,
  nextConsecutiveTimeouts,
  withinSnapshotBudget,
  heldSnapshotBytes,
  isSnapshotEnabled,
} from "./utils/file-snapshot";
import {
  getPricingLadder,
  getDefaultPrice,
  calculatePendingFilesPrice,
  applyCouponDiscount,
} from "./utils/price-utils";
import { uploadStepPrice } from "./utils/upload-step-price";
import { resumedOrderState } from "./utils/resumed-order";
import { recoverStaleCart as resetStaleCartSession } from "./utils/stale-cart-recovery";
import {
  saveOrderData,
  getOrderData,
  clearOrderData,
} from "src/utils/local-storage.util";
import {
  resolveOrderSession,
  resolveOrderId,
  readOrderSessionStores as readOrderSessionStores_,
  summariseSessionStores,
  type OrderSessionStores,
} from "src/utils/order-session";
import { formatTimeRemaining } from "src/utils/time.util";

// Create a limit for concurrent uploads
// Four PUTs at once. Three was sized for MinIO's connection slots; R2 has no
// such limit, and on a 2,000-photo order the extra lane is roughly a quarter
// off the wait. Kept modest: phones on mobile data gain little beyond this.
const limit = pLimit(4);

interface NewOrderProps {
  referer?: string;
}

// Add types and form interface at the top level
interface FormValues {
  photoSizeId: string;
}

interface OrderPhotoResponse {
  id: string;
  // Add other properties that might come from the response
}

// Minimum estimated upload time (seconds) for the "save time at checkout"
// prefill panel to be worth showing. Short uploads finish before it'd be useful.
const PREFILL_MIN_UPLOAD_SECONDS = 40;

const NewOrder: React.FC<NewOrderProps> = ({
  referer = "https://fotodelavnica.si",
}) => {
  const [user, setUser] = useState<GetUserDto | null>(null);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // New state to block uploads and show spinner during checkout
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const { files, setFiles } = useFiles();
  const { mainOrderId, guestToken } = useOrder();
  const { data: order } = useOrderQuery(mainOrderId, guestToken);
  const queryClient = useQueryClient();

  /**
   * Every place the active cart is persisted, in the order they are trusted.
   *
   * Handed to resolveOrderSession, which picks the first store holding a
   * COMPLETE (orderId, guestToken) pair and never mixes halves across stores.
   * `order?.id` is deliberately NOT a source: it is React Query cache, and the
   * query is keyed on mainOrderId anyway, so it can only ever repeat that id or
   * be stale. Pairing that stale id with whatever token happened to be around
   * is what produced the 401s in FOTO-NEXTJS-147.
   */
  const readOrderSessionStores = (): OrderSessionStores =>
    // Shared reader, not a local copy: this used to be assembled here and again
    // (differently, without localStorage) inside OrderProvider, so the two
    // disagreed about which stores even counted on the same page load.
    readOrderSessionStores_({ orderId: mainOrderId, guestToken });
  const { setCoupon } = useCoupon();
  const { openCartDrawer, closeCartDrawer, setCheckoutFunction } = useCartDrawer();
  const [oldOrder, setOldOrder] = useState<GetOrderDto | null>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const hasInitializedPhotoSize = useRef(false);
  const currency = getCurrencyBasedOnDomain();
  const [startTime, setStartTime] = useState(Date.now());
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(0);
  // Whether this upload is slow/long enough that the "save time at checkout"
  // panel is worth showing (see PREFILL_MIN_UPLOAD_SECONDS). Latched once the
  // estimate crosses the threshold; reset when the upload ends.
  const [prefillEligible, setPrefillEligible] = useState(false);
  const { downlink } = useConnectionSpeed();
  const [couponValid, setCouponValid] = useState(false);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingBuffer, setUploadingBuffer] = useState(0);
  const [uploadError, setUploadError] = useState<NestJSError | null>(null);

  // Calm inline notes about the picked photos, at the top of the photo
  // section. They replaced browser alert()s: an alert blocks the page and reads
  // as an error, and most of these are not the customer's doing. See
  // utils/upload-notes.ts.
  const [uploadNotes, setUploadNotes] = useState<UploadNote[]>([]);
  const uploadNotesRef = useRef<HTMLDivElement>(null);
  // Set when a note answers a button press: the customer is looking at the
  // button, not the top of the section, so the note is brought into view.
  const revealNotesRef = useRef(false);
  const addUploadNote = useCallback(
    (note: UploadNote, { reveal = false }: { reveal?: boolean } = {}) => {
      if (reveal) revealNotesRef.current = true;
      setUploadNotes((prev) => mergeNote(prev, note));
    },
    [],
  );
  const dismissUploadNote = useCallback((key: string) => {
    setUploadNotes((prev) =>
      prev.filter((n) => (n.kind === "message" ? `message:${n.id}` : n.kind) !== key),
    );
  }, []);
  useEffect(() => {
    if (!revealNotesRef.current || uploadNotes.length === 0) return;
    revealNotesRef.current = false;
    uploadNotesRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [uploadNotes]);
  // Only inside a social app's WebView is "open it in Chrome or Safari" good
  // advice; in Safari itself it blamed the wrong thing.
  const inAppBrowser = useInAppBrowser().app !== null;
  // The red "nothing was added" box sits below the picker, often under the
  // sticky button on a phone: bring it into view rather than alert() it.
  useEffect(() => {
    if (!uploadError) return;
    document.getElementById(UPLOAD_ERROR_ID)?.scrollIntoView?.({
      behavior: "smooth",
      block: "center",
    });
  }, [uploadError]);
  // Picks restored after a reload. One of these that cannot be read is a pick
  // to make again (its stored copy is gone), not an error.
  const restoredPickIdsRef = useRef(new Set<string>());

  // For order creation and cart management
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [currentOrderPhotoId, setCurrentOrderPhotoId] = useState<string | null>(
    null
  );
  const [selectedGuestToken, setSelectedGuestToken] = useState<string | null>(null);
  const [showSelectSizeDialog, setShowSelectSizeDialog] = useState(false);

  // Dialog for prompting after upload
  const [showUploadMoreDialog, setShowUploadMoreDialog] = useState(false);

  // Track failed authentication attempts to prevent infinite loops
  const [failedAuthAttempts, setFailedAuthAttempts] = useState<Set<string>>(
    new Set()
  );

  // Auto calculate connection-based upload parameters
  let concurrentUpload = 2;
  let batchUploadSize = 2;
  if (downlink && downlink < 2) {
    concurrentUpload = 1;
    batchUploadSize = 2;
  } else if (downlink && downlink < 5) {
    concurrentUpload = 3;
    batchUploadSize = 3;
  } else if (downlink && downlink < 10) {
    concurrentUpload = 5;
    batchUploadSize = 5;
  } else if (downlink && downlink >= 10) {
    concurrentUpload = 5;
    batchUploadSize = 10;
  }

  const {
    uploading: originalUploading,
    uploadProgress: originalUploadProgress,
    uploadingBuffer: originalUploadingBuffer,
    uploadError: originalUploadError,
    startUpload,
  } = useUpload();

  const showRemainingTime = disableSubmit && uploadProgress > 0;
  const minimumOrder = MINIMUM_ORDER_CENTS / 100;

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Extract locale from pathname (Next.js App Router includes it in pathname)
  // For example: "/sl/order" has locale "sl"
  const currentLocale = pathname?.split("/")[1] || "sl";
  const theme = useTheme();
  let currentLocaleFixed =
    currentLocale === "sl" ? "si" : currentLocale || "si";
  currentLocaleFixed = currentLocaleFixed.split("-")[0];

  // Get order info from order context
  const {
    setOrderInfo,
    ensureOrderExists,
    restoreWasReconciled,
    rescuedGroupIdsSnapshot,
    dismissRestoreReconciled,
  } = useOrder();
  const removeRescuedGroupMutation = useRemoveOrderPhoto();
  const [removingGroupId, setRemovingGroupId] = useState<string | null>(null);
  const [removeRescuedError, setRemoveRescuedError] = useState<string | null>(
    null,
  );

  // Groups already on the order when this page load restored an interrupted
  // upload. Built from the order query, which already carries a signed url per
  // photo (PhotoSignInterceptor), so showing them costs no extra request.
  // Only the groups whose own cached count understates what they hold. Showing
  // every group with photos would say "we saved 8" when 6 were already the
  // customer's from an earlier visit and only 2 were actually rescued.
  // From the provider's snapshot, NOT recomputed here: the reconcile repairs
  // the counts that identify these groups, and this component's order query is
  // a separate request that may not have resolved yet.
  const rescuedIds = rescuedGroupIdsSnapshot;
  const rescuedGroups: RescuedGroup[] = restoreWasReconciled
    ? (order?.orderPhotos ?? [])
        .filter((group) =>
          rescuedIds.includes((group as { id?: string }).id ?? ""),
        )
        .map((group) => ({
          id: (group as { id: string }).id,
          sizeLabel:
            (group as { photoSize?: { name?: Record<string, string> } })
              .photoSize?.name?.[currentLocaleFixed] ??
            (group as { photoSize?: { name?: Record<string, string> } })
              .photoSize?.name?.en ??
            "",
          // Cast through unknown: the generated client declares photo.id as a
          // number, but the API returns a uuid string. Coerced rather than
          // "corrected" here, because regenerating the client is a separate
          // change with its own blast radius.
          photos: (
            ((group as unknown as {
              photos?: {
                id: unknown;
                name?: string;
                url?: string;
                count?: number;
              }[];
            }).photos ?? [])
          ).map((p) => ({
            id: String(p.id),
            name: p.name,
            url: p.url,
            // Carried so the notice can total PRINTS rather than rows. A photo
            // ordered in duplicate has count > 1, and the reconcile this notice
            // announces counts the same way -- totalling rows here would make
            // the notice contradict the cart it is explaining.
            count: p.count,
          })),
        }))
        .filter((g) => g.photos.length > 0)
    : [];

  const handleRemoveRescuedGroup = async (groupId: string) => {
    if (!mainOrderId || !guestToken) return;
    setRemovingGroupId(groupId);
    setRemoveRescuedError(null);
    try {
      await removeRescuedGroupMutation.mutateAsync({
        orderId: mainOrderId,
        orderPhotoId: groupId,
        guestToken,
      });
    } catch (error) {
      // Without this the rejection is unhandled: the spinner stops, the group
      // stays, and nothing tells the customer why. This button is the escape
      // hatch for someone who cannot work out which photos are already here,
      // so a dead button leaves them stuck with no way forward.
      Sentry.captureException(error, {
        tags: { area: "photo-upload", stage: "remove-rescued-group" },
        extra: { orderId: mainOrderId, orderPhotoId: groupId },
      });
      setRemoveRescuedError(
        translate(
          "rescuedUpload.removeFailed",
          "We could not remove those photos. Please try again.",
        ),
      );
    } finally {
      setRemovingGroupId(null);
    }
  };

  // Initialize photo sizes hook
  const photoSizes = usePhotoSizes();

  // Create compatible autocompleteProps object
  const autocompleteProps = {
    options: photoSizes.data ?? [],
  };
  const photoSizeOptionsRef = useRef(autocompleteProps.options);
  photoSizeOptionsRef.current = autocompleteProps.options;

  // Initialize form for photo size selection with correct type
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { photoSizeId: "" } });

  // Fetch user profile on component mount — skip entirely for guests
  // (no bearerToken cookie) so we don't trigger noisy 401s on the home page.
  useEffect(() => {
    if (!nookies.get(null).bearerToken) return;
    const fetchUser = async () => {
      try {
        const response = await userApi.userControllerGetMe();
        setUser(response.data as unknown as GetUserDto);
      } catch (error: unknown) {
        // Silently ignore 401 — interceptor already cleared cookies.
        if (
          (error as { response?: { status?: number } })?.response?.status !==
          401
        ) {
          console.error("Failed to fetch user profile:", error);
        }
      }
    };
    fetchUser();
  }, []);


  useEffect(() => {
    if (autocompleteProps.options.length === 0) return;
    if (hasInitializedPhotoSize.current) return;

    hasInitializedPhotoSize.current = true;

    // Check if photoSizeId is passed via URL params
    const urlPhotoSizeId = getUrlParameter("photoSizeId");

    if (urlPhotoSizeId) {
      const isValidPhotoSize = autocompleteProps.options.some(
        (option) => option.id === urlPhotoSizeId
      );

      if (isValidPhotoSize) {
        setValue("photoSizeId", urlPhotoSizeId);
        return;
      }
    }

    // Fall back to default size if no valid URL param
    const photoSizeMala = autocompleteProps.options.find((option) =>
      DEFAULT_IMAGE_SIZE_NAME.includes(
        option.name[currentLocaleFixed ?? "sl"]
      )
    );
    const photoSizeDefaultId = photoSizeMala ? photoSizeMala.id : "";
    if (!photoSizeDefaultId) return;
    setValue("photoSizeId", photoSizeDefaultId);
  }, [autocompleteProps.options]);

  // A link that names a size after the page has mounted (the bundle slots
  // panel, the cart drawer) must still select it: the effect above runs once
  // per page load.
  const paramPhotoSizeId = searchParams?.get("photoSizeId") ?? null;
  useUrlPhotoSize(paramPhotoSizeId, autocompleteProps.options, (id) =>
    setValue("photoSizeId", id)
  );

  // Persist order data to localStorage when order is ready
  useEffect(() => {
    if (!mainOrderId || !guestToken) return;
    return () => {
      try {
        saveOrderData(mainOrderId, guestToken);
      } catch (e) {
        console.error("Error saving order data to localStorage", e);
      }
    };
  }, [mainOrderId, guestToken]);

  // Register checkout function with cart drawer context
  useEffect(() => {
    setCheckoutFunction(() => handleContinueToCheckout);

    // Cleanup function to remove checkout function when component unmounts
    return () => {
      setCheckoutFunction(undefined);
    };
  }, [setCheckoutFunction]);

  const photoSizeId = watch("photoSizeId");
  const photoSize = autocompleteProps.options.find(
    (option) => option.id === photoSizeId
  );
  const photoSizeIdRef = useRef(photoSizeId);
  photoSizeIdRef.current = photoSizeId;
  // The size picks are being chosen for, so a reload can preselect it. Not
  // while an upload names its group: those picks keep that upload's size.
  useEffect(() => {
    if (photoSizeId) void pendingPicks().describe({ photoSizeId });
  }, [photoSizeId]);

  const translate = useAppTranslate();
  // Function to create a new order or use existing one, then handle upload.
  // `resume` is set when picks offered back after a reload start this upload:
  // they go back into the group they were picked for (see resume-upload.ts).
  const handleCreateOrderAndUpload = async (
    resume?: { target: PickTarget },
  ) => {
    if (files.length === 0) {
      addUploadNote(
        { kind: "message", id: "uploadOnePhoto", text: translate("order.uploadOnePhoto") },
        { reveal: true },
      );
      return;
    }

    if (!photoSizeId) {
      addUploadNote(
        { kind: "message", id: "selectPhotoSize", text: translate("order.selectPhotoSize") },
        { reveal: true },
      );
      return;
    }

    // Probe every file still on a live handle BEFORE creating anything. A
    // handle that has already died (in-app WebView reclaim, evicted cloud
    // placeholder) would otherwise burn six retries per photo and surface
    // only at the end, as a count, after the customer has waited for the lot.
    // Snapshotted files are in memory and cannot die. See probe-handles.ts.
    setDisableSubmit(true);
    const liveHandles = files.filter((f) => !f.bytes).map((f) => f.file);
    let deadFiles = new Set<File>();
    try {
      const statuses = await probeFileHandles(liveHandles, {
        concurrency: detectCurrentInAppBrowser().isInApp ? 2 : 4,
      });
      deadFiles = new Set(liveHandles.filter((_, i) => statuses[i] === "dead"));
    } catch {
      // Probing is a courtesy. If it cannot run, upload as before.
    }
    if (deadFiles.size > 0) {
      const deadNames = files.filter((f) => deadFiles.has(f.file)).map((f) => f.file.name);
      Sentry.captureMessage("Picked files unreadable before upload (asked to re-pick)", {
        level: "warning",
        tags: { area: "photo-upload", inAppBrowser: inAppBrowserTag() },
        extra: { dead: deadFiles.size, total: files.length, names: deadNames.slice(0, 20) },
      });
      // Removed and named, and the upload is NOT started: re-picking them now
      // puts them in this same item, instead of a second cart line later.
      setFiles((prev) => prev.filter((f) => !deadFiles.has(f.file)));
      setDisableSubmit(false);
      addUploadNote({ kind: "repick", names: deadNames }, { reveal: true });
      return;
    }

    // From here these picks belong to the upload: a snapshot run still in
    // progress must not save them for reload (see snapshotAdded).
    files.forEach((f) => f.pickId && uploadingPickIdsRef.current.add(f.pickId));

    // Check if all files are already uploaded
    const filesToUpload = files;

    // Declared out here because the catch below needs them to clean up after
    // itself. Set once add-order-photos has created a group, so the failure
    // path knows there is an orphan to remove -- and, critically, WHICH one.
    // Only ever filled in when the group can be named exactly; a guess must
    // never be handed to a DELETE.
    let createdOrderPhotoId: string | null = null;
    let orderIdForCleanup = "";
    let orderTokenForCleanup = "";
    // Flipped the moment startUploadWithOrderPhoto RETURNS. It throws unless at
    // least one photo was registered (photos that landed unregistered throw a
    // customer-facing error carrying landedCount), so this is the difference between "the
    // group has the customer's photos" and "the group is empty" -- and
    // therefore between cleaning up and destroying their work.
    let uploadAttachedPhotos = false;
    // Tells the delivery step, in any tab, that photos are on their way from
    // this one (utils/upload-activity.ts). Held by the upload itself, not the
    // screen, so it lasts exactly as long as the photos are being sent.
    const releaseUploadActivity = holdUploadActivity();
    // Photos of this run whose bytes reached storage. If the upload still
    // throws (e.g. none could be registered yet), the server's sweep adds
    // them to the cart, so they leave the selection: keeping them selected
    // invites the customer to upload them a second time.
    const landedFiles = new Set<File>();
    const uploadHooks = {
      onFileLanded: (index: number) => {
        if (filesToUpload[index]) landedFiles.add(filesToUpload[index].file);
        void forgetLandedPick(filesToUpload, index);
      },
      onFileSending: (index: number, check: { headUrl: string; size: number }) =>
        void notePickSending(filesToUpload, index, check),
      onFilesPresigned: (entries: { index: number; key: string }[]) =>
        void notePicksPresigned(filesToUpload, entries),
    };

    try {
      setDisableSubmit(true);
      setIsUploading(true);

      // Resolve the active cart as a PAIR, from a single store.
      //
      // These two used to be resolved independently -- the id from
      // `order?.id || mainOrderId` (React Query cache first) and the token from
      // `guestToken || urlGuestToken`, a chain that never consulted the cookie
      // even though the sibling checkout handler did. So the id could resolve
      // off cached data while the token came back empty, and the upload fired
      // anyway: the backend's OrderOwnerGuard answered "No authentication token
      // provided" and the customer lost the batch (FOTO-NEXTJS-147, 43 events).
      // Now a store is used only when it holds BOTH halves.
      //
      // The one exception is a SIGNED-IN customer: their order is authorised by
      // the bearer JWT and never has a guest token, so a bare order id is a
      // whole session for them and must still resume. A guest holding a bare id
      // has nothing to prove ownership with, so resuming it can only produce
      // the 401 above — starting a fresh order gets them a working upload
      // instead of a failed one.
      const sessionStores = readOrderSessionStores();
      const session = resolveOrderSession(sessionStores);
      const signedIn = Boolean(nookies.get(null).bearerToken);
      let orderId =
        session?.orderId ?? (signedIn ? (resolveOrderId(sessionStores) ?? "") : "");
      let orderToken = session?.guestToken ?? "";
      let orderPhotoId: string | undefined;

      // The groups this order held BEFORE we touch it, straight from the
      // server. `null` means we never got a trustworthy answer -- see
      // pickNewOrderPhotoId for why that must not be spelled as `[]`.
      let baselineOrderPhotoIds: string[] | null = null;
      // The groups themselves, from the same pre-flight answer, for a resume
      // looking for the group its picks belong to. Null when not known.
      let preflightGroups: GetOrderDto["orderPhotos"] | null = null;

      // Pre-flight: if we're resuming a saved order, make sure it's still
      // usable BEFORE uploading. A dead order (expired guest token, terminal
      // status, or photos already purged by the cleanup cron) would otherwise
      // accept the whole upload and only fail later ("old session"), wasting
      // the customer's effort. Detect it now and silently start a fresh order.
      // Set when the pre-flight below throws the resumed order away. The
      // creation report further down is then suppressed: the discard has
      // already cleared the cookies, so it would fire a second, misleading
      // "half-lost session" event for what is really one incident.
      let discardedThisRun = false;

      if (orderId) {
        let staleResumedOrder = false;
        // Captured for diagnostics: WHY we discarded, and what it costs the
        // customer. See src/utils/order-loss-diagnostics.ts -- this is the
        // event behind ~8 stranded orders/day.
        let discardReason: OrderDiscardReason | null = null;
        let discardHttpStatus: number | undefined;
        let abandonedPhotoCount = 0;
        try {
          const check = await orderApi.getOneBaseOrderControllerOrder(
            orderId,
            orderToken ?? ""
          );
          const resumed = check.data as unknown as GetOrderDto;
          // The authoritative "before" picture, fetched moments ago with this
          // very token. Captured here so identifying the new group costs no
          // extra request and never depends on the React Query cache, which is
          // keyed on the CONTEXT order id and is simply absent whenever the
          // session was resolved from the URL, a cookie or localStorage.
          baselineOrderPhotoIds = (resumed.orderPhotos ?? [])
            .map((op) => op.id)
            .filter((id): id is string => typeof id === "string" && !!id);
          preflightGroups = resumed.orderPhotos ?? [];
          // A selected bundle counts as content: "Izberi paket" creates an
          // empty order to hold it (see ./utils/resumed-order).
          const { stale, terminal, livePhotoCount } =
            resumedOrderState(resumed);
          staleResumedOrder = stale;
          if (staleResumedOrder) {
            discardReason = terminal ? "terminal-status" : "no-live-photos";
            // Trust the server's own count over the join when they disagree:
            // a stale-looking order that still reports photos is the case we
            // most need to see.
            abandonedPhotoCount = Math.max(
              livePhotoCount,
              resumed.totalNOfPhotos ?? 0
            );
          }
        } catch (err) {
          const status = (err as { response?: { status?: number } })?.response
            ?.status;
          // This pre-flight is the ONE question that answers for both kinds of
          // customer: OrderOwnerGuard accepts either the bearer JWT or the
          // guest token, so "can this session use this order?" gets a single
          // definitive answer here, whoever is asking.
          //
          // A definitive 404 (gone server-side) or 401/403 (cannot prove
          // ownership) both mean the same thing for what happens next: the
          // upload cannot succeed on this order. Firing it anyway is what
          // produced FOTO-NEXTJS-147 / 14P — the request went out with a
          // credential we had ALREADY been told was no good, and the customer
          // lost the batch after picking their photos.
          //
          // 401/403 used to be swallowed here on the theory that it was a
          // transient guest-token read miss. That reasoning belonged to the old
          // code, which assembled the id and token from separate stores: a read
          // miss could leave a real id paired with an empty token. It cannot
          // now — resolveOrderSession only ever returns a COMPLETE pair, so a
          // miss yields no session at all and we never reach this call. A 401
          // on a complete pair is therefore a real answer, not a glitch. For a
          // signed-in customer it is doubly settled: axios.ts has already tried
          // /auth/refresh and failed, and has DELETED the bearer cookie on the
          // way past — the credential this upload would use no longer exists.
          //
          // Starting a fresh order does not delete the old one; it stays on the
          // server untouched. We simply stop pretending we can reach it.
          const preflightReason = discardReasonForPreflightStatus(status);
          if (preflightReason) {
            staleResumedOrder = true;
            discardHttpStatus = status;
            discardReason = preflightReason;
            // The check failed, so we cannot know what is on the order. Fall
            // back to whatever the client last knew, so the report still
            // carries a cost estimate instead of a misleading zero.
            abandonedPhotoCount = order?.totalNOfPhotos ?? 0;
          }
          // A transient NETWORK error (no response, so no status) still keeps
          // the order: that is a genuine "we don't know", and guessing would
          // orphan a live cart on a dropped connection.
        }

        if (staleResumedOrder) {
          if (discardReason) {
            reportOrderDiscarded({
              reason: discardReason,
              orderId,
              abandonedPhotoCount,
              hadGuestToken: Boolean(orderToken),
              // Which store the pair came from. Narrows a lost session to the
              // layer that lost it (state vs URL vs cookie vs localStorage).
              tokenSource: session?.source ?? "none",
              httpStatus: discardHttpStatus,
            });
          }
          discardedThisRun = true;
          console.warn(
            "Resumed order is stale; starting a fresh order before upload."
          );
          clearOrderData();
          setGuestTokenCookies("");
          setOrderInfo(null, null);
          setSelectedOrderId(null);
          setSelectedGuestToken(null);
          // Fall through to the brand-new-order branch below.
          orderId = "";
          orderToken = "";
          // Belongs to the order we just threw away; it must not be carried
          // into anything that runs after this point.
          baselineOrderPhotoIds = null;
          preflightGroups = null;
        }
      }

      if (!orderId) {
        // Diagnostics: a brand-new order here is normal for a first-time
        // visitor, but NOT if the browser still carries traces of a previous
        // one. A split cookie pair (order id kept, guest token gone, or vice
        // versa) is the signature of the session being half-lost -- the
        // restore in OrderProvider needs BOTH and silently gives up otherwise,
        // which strands whatever the customer already uploaded.
        if (!discardedThisRun) {
          const cookiesNow = nookies.get(null);
          // Identify the order this path would displace. Deliberately NOT
          // passing displacedPhotoCount here: that field drives severity, and
          // adding it would turn this call's warnings into errors. This change
          // is diagnostic only — decide on the alerting once the status tag has
          // shown which of these are real.
          const displaced = order as
            | { id?: string; status?: string }
            | undefined;
          reportSuspiciousOrderCreation({
            hadPriorOrderId: Boolean(order?.id || mainOrderId),
            cookieHadOrderId: Boolean(cookiesNow.orderId),
            cookieHadGuestToken: Boolean(cookiesNow.guest_token),
            origin: "upload",
            displacedOrderId: displaced?.id,
            displacedOrderStatus: displaced?.status,
            // The four stores exactly as this upload read them a few lines
            // above -- reused, not re-read, so the report describes the same
            // instant the decision was made from and cannot drift from it.
            stores: summariseSessionStores(sessionStores),
          });
        }

        // Get tracking parameters
        const fbc = getUrlParameter("fbclid");
        const fbp = nookies.get(null)._fbp;
        const domainName = getDomain();
        const { getStoredGclid } = await import("src/utils/gclid-tracker");
        const gclid = getStoredGclid();

        // Create a new order with the photoSize
        const createOrderDto: CreateUserOrderDTO = {
          photoSizeId, // Include the photoSize for the initial order
          userId: user?.id ?? "",
          fbc: fbc,
          fbp: fbp,
          gclid: gclid ?? undefined,
          currency: currency,
          downlink: downlink,
          domain: domainName,
          languagePreference: currentLocaleFixed ?? "en",
          orderSource: "WEB", // order origin channel ('WEB' | 'MOBILE'); mobile app sends 'MOBILE'
        };

        const response = await orderApi.orderControllerCreateOrder(
          createOrderDto,
          "",
          // x-client-channel: unset on web; the server derives it from the domain.
          undefined,
          { withCredentials: true }
        );

        const orderReceived = response.data as unknown as GetOrderDto & {
          guestToken: string;
        };
        orderId = orderReceived.id;
        orderToken = orderReceived.guestToken;

        // Picks saved before this order existed live under "new". After a
        // reload the page asks for this order's key, so move them now -- the
        // ones that do not finish uploading are exactly what must be offered
        // back. Snapshot saves from here on use the order's key too. Bounded
        // (REKEY_TIMEOUT_MS): storage that never answers cannot hold the upload.
        await pendingPicks().rekey(pickSessionKey(null), pickSessionKey(orderId));
        pickSessionRef.current = pickSessionKey(orderId);

        setSelectedOrderId(orderId);
        setCurrentOrderPhotoId(orderReceived.orderPhotos[0]?.id);
        setSelectedGuestToken(orderToken);
        setGuestTokenCookies(orderToken ?? "");
        if (orderReceived?.id) {
          queryClient.setQueryData(orderQueryKey(orderReceived.id), orderReceived);
        }

        // Initialize the product context with the new order info
        setOrderInfo(orderId, orderToken);

        // Update URL with order info for persistence without reloading the page
        const params = new URLSearchParams(searchParams?.toString() || "");
        params.set("orderId", String(orderId));
        if (orderToken) {
          params.set("guest_session_token", orderToken);
        }
        router.replace(`${pathname || ""}?${params.toString()}`);

        // Upload using the standard method for the first photo group
        if (files.length > 0) {
          setStartTime(Date.now());
          // Where these picks are going, for a reload to send them back there.
          void pendingPicks().describe({
            photoSizeId,
            orderPhotoId: orderReceived.orderPhotos[0]?.id ?? null,
          });
          await startUploadWithOrderPhoto(
            files,
            orderId || "",
            orderReceived.orderPhotos[0]?.id,
            limit,
            setUploading,
            setUploadProgress,
            setUploadingBuffer,
            setUploadError,
            concurrentUpload,
            batchUploadSize,
            translate,
            uploadHooks,
          );
          // Returned, so at least one photo was registered against the group.
          uploadAttachedPhotos = true;
        }
      } else {
        // A resume goes back into the group its picks were picked for, or
        // another group of that size, judged from the pre-flight answer
        // fetched moments ago. Nothing is created and nothing will be cleaned
        // up: createdOrderPhotoId stays null, so a failure never deletes it.
        const resumeGroup = resume
          ? chooseResumeGroup(preflightGroups, resume.target, photoSizeId)
          : null;
        if (resumeGroup) {
          orderPhotoId = resumeGroup.id;
          if (resumeGroup.reason !== "original") {
            Sentry.addBreadcrumb({
              category: "photo-upload",
              message: "Resumed picks go to an existing group of the same size",
              data: { orderId, orderPhotoId, wanted: resume?.target.orderPhotoId },
            });
          }
          if (files.length > 0) {
            setStartTime(Date.now());
            void pendingPicks().describe({ photoSizeId, orderPhotoId });
            await startUploadWithOrderPhoto(
              files,
              orderId,
              orderPhotoId,
              limit,
              setUploading,
              setUploadProgress,
              setUploadingBuffer,
              setUploadError,
              concurrentUpload,
              batchUploadSize,
              translate,
              { ...uploadHooks, firstRank: resumeGroup.firstRank },
            );
            uploadAttachedPhotos = true;
          }
        } else {
          // Order exists, create a new OrderPhoto entry
          try {
            // Which groups the order held before this call.
            //
            // The pre-flight above is the trusted source; the React Query cache
            // is only a fallback, and ONLY when it actually holds data. It used
            // to be the sole source, spelled `order?.orderPhotos || []`, and that
            // `|| []` is what produced the empty groups: `order` is undefined
            // whenever the session came from the URL / cookie / localStorage
            // rather than context (routine on mobile, where an evicted cookie is
            // enough), the baseline collapsed to "the order has no groups", and
            // the diff below then named orderPhotos[0] -- the OLDEST group -- as
            // the new one. Every photo went to the first group and the group just
            // created stayed empty. Nothing threw, so nothing was reported.
            if (baselineOrderPhotoIds === null && Array.isArray(order?.orderPhotos)) {
              baselineOrderPhotoIds = order.orderPhotos
                .map((op) => op.id)
                .filter((id): id is string => typeof id === "string" && !!id);
            }

            // Call your new API endpoint to add a new OrderPhoto
            const orderPhotoResponse =
              await orderApi.orderControllerAddOrderPhotos(
                orderId,
                {
                  photoSizeId: photoSizeId,
                },
                orderToken ?? ""
              );

            const responseData = orderPhotoResponse.data as unknown as GetOrderDto;

            // Log the complete response for debugging
            console.log("OrderPhoto response:", responseData);

            // Make sure we have a valid ID, or use a fallback
            if (!responseData || !responseData.id) {
              console.error(
                "Failed to get valid orderPhotoId from response",
                responseData
              );
              throw new Error("Invalid orderPhoto response");
            }

            // Find the newly created orderPhoto by diffing against the baseline.
            const pick = pickNewOrderPhotoId(
              baselineOrderPhotoIds,
              responseData.orderPhotos
            );

            if (!pick.id) {
              // We cannot name the new group, so we will not attach photos to a
              // guess. A group was almost certainly created; deliberately NOT
              // cleaned up, because "cannot tell which group is which" is exactly
              // the state in which a DELETE could take the customer's photos.
              // It is left behind and hidden from the cart instead.
              // captureMessage via the shared reporter, not captureException:
              // nothing threw here, and the stack of a handler we already know
              // adds nothing. What is worth having is the DECISION -- above all
              // `baselineKnown`, which is the `|| []` that caused this whole
              // class of bug -- and that now arrives as filterable tags on a
              // stable title. See order-loss-diagnostics.ts.
              reportPhotoGroupIdentification({
                confidence: "none",
                orderId,
                baselineKnown: baselineOrderPhotoIds !== null,
                baselineCount: baselineOrderPhotoIds?.length ?? null,
                hadOrderInCache: Boolean(order?.orderPhotos),
                responseGroupCount: responseData.orderPhotos?.length ?? null,
                tokenSource: session?.source ?? "none",
              });
              throw new Error("Failed to identify newly created orderPhoto");
            }

            if (pick.confidence === "degraded") {
              // Guessed (last group in the response) because no baseline was
              // available. Usable, but this is the signal that the session did
              // not resolve cleanly -- report it rather than letting it hide.
              reportPhotoGroupIdentification({
                confidence: "degraded",
                orderId,
                orderPhotoId: pick.id,
                // `degraded` is reached only when there was no baseline, so this
                // is false by construction -- sent anyway, because the tag is
                // what makes the two paths comparable in one Sentry search.
                baselineKnown: baselineOrderPhotoIds !== null,
                baselineCount: baselineOrderPhotoIds?.length ?? null,
                hadOrderInCache: Boolean(order?.orderPhotos),
                responseGroupCount: responseData.orderPhotos?.length ?? null,
                tokenSource: session?.source ?? "none",
              });
            }

            orderPhotoId = pick.id;
            // Only an EXACT match may later be deleted: a guessed id could name a
            // group that already holds photos.
            if (pick.confidence === "exact") {
              createdOrderPhotoId = pick.id;
              orderIdForCleanup = orderId;
              orderTokenForCleanup = orderToken ?? "";
            }

            // Upload using the custom method that supports orderPhotoId
            if (files.length > 0) {
              setStartTime(Date.now());
              console.log("Starting upload with orderPhotoId:", orderPhotoId);
              void pendingPicks().describe({ photoSizeId, orderPhotoId });
              await startUploadWithOrderPhoto(
                files,
                orderId,
                orderPhotoId || "",
                limit,
                setUploading,
                setUploadProgress,
                setUploadingBuffer,
                setUploadError,
                concurrentUpload,
                batchUploadSize,
                translate,
                uploadHooks,
              );
              // Returned, so at least one photo was registered against the group.
              uploadAttachedPhotos = true;
            }
          } catch (error) {
            console.error("Error creating OrderPhoto:", error);
            Sentry.captureException(error);
            throw error;
          }
        }
      }

      // Clear the files after upload
      setFiles([]);
      // Done: a later selection is not headed for this group unless its own
      // upload says so.
      void pendingPicks().describe({
        orderPhotoId: null,
        photoSizeId: photoSizeIdRef.current || null,
      });

      // Show the dialog asking if they want to upload more
      // setShowUploadMoreDialog(true);
      openCartDrawer();
      if (orderId) {
        console.log("Order created or updated successfully:", orderId);
        try {
          // Compute + persist the price now so the cart shows the real total
          // right after upload, instead of 0.00 until the checkout click.
          // Same calculateAmount + cache pattern as handleContinueToCheckout.
          const responseAmount =
            await orderApi.orderControllerCalculateAmount(orderId);
          const orderWithAmount = responseAmount.data as unknown as GetOrderDto;
          if (orderWithAmount?.id) {
            queryClient.setQueryData(
              orderQueryKey(orderWithAmount.id),
              orderWithAmount
            );
          }
        } catch (calcError) {
          // Price calc must never turn a successful upload into an error.
          // Fall back to the previous behavior (plain refetch; total then shows
          // on the checkout click as before).
          Sentry.captureException(calcError);
          const dataData = await orderApi.getOneBaseOrderControllerOrder(
            orderId,
            orderToken || ""
          );
          const orderReceived = dataData.data;
          if (orderReceived?.id) {
            queryClient.setQueryData(
              orderQueryKey(orderReceived.id),
              orderReceived
            );
          }
        }
      }

      setDisableSubmit(false);
      setIsUploading(false);
    } catch (error: unknown) {
      // The selection stays after a failed upload, so its picks may be saved
      // for reload again (the ones that landed were already forgotten).
      filesToUpload.forEach(
        (f) =>
          f.pickId &&
          !landedFiles.has(f.file) &&
          uploadingPickIdsRef.current.delete(f.pickId),
      );
      if (landedFiles.size > 0) {
        setFiles((prev) => prev.filter((f) => !landedFiles.has(f.file)));
      }
      const err = error as {
        response?: { data?: { message?: string; statusCode?: number } };
      };
      const errorMsg = err?.response?.data?.message;
      const errorCode = err?.response?.data?.statusCode;
      console.error("Upload error:", errorMsg, "Error " + errorCode);
      console.error("There has been a problem with your operation:", error);
      Sentry.captureException(error);

      // Nothing attached, so the group we created is an orphan: it renders as
      // an empty cart line, it makes the order look non-empty to every later
      // check, and at payment the backend rejects it outright ("The photos for
      // this order never finished uploading"). Remove it.
      //
      // Three conditions, all required, because the cost of getting this wrong
      // is deleting a customer's photos: we created it in THIS run, we can name
      // it exactly (never a guess), and the upload demonstrably registered
      // nothing. Even then the server is asked to confirm the group is empty
      // before the DELETE goes out -- if that check cannot be made, the empty
      // group is simply left behind, which fix #3 already hides.
      // Photos that reached storage are registered by the server's sweep even
      // when our own registration failed, so a group holding any of them must
      // stay. Show what the server has instead of deleting it.
      const deleteCreatedGroup = shouldDeleteEmptyGroup({
        createdOrderPhotoId,
        uploadAttachedPhotos,
        landedCount: (error as { landedCount?: number })?.landedCount,
      });
      if (createdOrderPhotoId && !uploadAttachedPhotos && !deleteCreatedGroup) {
        queryClient.invalidateQueries({
          queryKey: orderQueryKey(orderIdForCleanup),
        });
      }
      if (createdOrderPhotoId && deleteCreatedGroup) {
        // Named before the cleanup runs, and independently of whether it
        // succeeds. The exception above already says the upload failed; it does
        // not say a group was created and left holding nothing, or WHICH group
        // -- and that is the only part someone can go and check against the
        // order. Staging order 7f69e9c2 had seven groups and six were empty
        // before anyone knew to look.
        reportEmptyPhotoGroup({
          orderId: orderIdForCleanup,
          orderPhotoId: createdOrderPhotoId,
          // A count. Deliberately not the names, sizes or previews.
          fileCount: filesToUpload.length,
        });
        try {
          const verify = await orderApi.getOneBaseOrderControllerOrder(
            orderIdForCleanup,
            orderTokenForCleanup
          );
          const verifyOrder = verify.data as unknown as GetOrderDto;
          const group = (verifyOrder.orderPhotos ?? []).find(
            (op) => op.id === createdOrderPhotoId
          );
          if (group && !hasAttachedPhotos(group)) {
            await removeOrderPhoto(
              orderIdForCleanup,
              createdOrderPhotoId,
              orderTokenForCleanup
            );
            queryClient.invalidateQueries({
              queryKey: orderQueryKey(orderIdForCleanup),
            });
          }
        } catch (cleanupError) {
          // Leaving the empty group behind is the safe outcome, so a failed
          // cleanup must never become the error the customer sees.
          console.error("Failed to clean up the empty photo group", cleanupError);
          Sentry.captureException(cleanupError, {
            level: "warning",
            tags: { area: "photo-upload" },
            extra: { orderId: orderIdForCleanup, createdOrderPhotoId },
          });
        }
      }

      // Never fail silently.
      //
      // This catch runs only when NOTHING was added to the cart, and it used to
      // do nothing the customer could see: it reset the button and returned, so
      // the upload "appeared to do nothing" -- the single worst part of this
      // bug. The red box below the upload section is easy to miss on a phone
      // (it sits under a sticky button, off-screen), so it is scrolled into
      // view (see the uploadError effect) -- not a blocking alert.
      const customerFacing =
        (error as { customerFacing?: boolean })?.customerFacing === true &&
        error instanceof Error &&
        !!error.message;
      const message = customerFacing
        ? (error as Error).message
        : translate(
            "order.uploadFailedNothingAdded",
            "Fotografij ni bilo mogoče dodati v košarico. Prosimo, poskusite znova."
          );
      setUploadError({
        statusCode: errorCode ?? 0,
        message,
        error: "UPLOAD_FAILED",
      });

      setDisableSubmit(false);
      setIsUploading(false);
    } finally {
      releaseUploadActivity();
    }
  };

  // Calculate total photos from current files
  const totalPhotos = files.reduce((total, file) => {
    return total + (file.count || 1);
  }, 0);

  // The bottom bar: the cart as it stands plus the photos still waiting to be
  // added. It used to count only the waiting ones, which the upload empties
  // as soon as it lands, so it read 0,00 € on every non-empty cart while the
  // drawer showed the real total (see upload-step-price.ts).
  const { priceCents: currentPriceCents, goodsCents: currentGoodsCents } =
    React.useMemo(
      () =>
        uploadStepPrice({
          order,
          pendingCents: calculatePendingFilesPrice(files, photoSize, currency),
          photoSizeSelected: !!photoSize,
        }),
      [order, files, photoSize, currency],
    );
  const currentPrice =
    currentPriceCents === null ? null : currentPriceCents / 100;
  // Handle file upload
  // Running total of bytes currently held as in-memory snapshots, kept in sync
  // with the files list so the budget is GLOBAL across every add action rather
  // than reset per drop. Without this, adding 300 photos in 6 drops of 50 would
  // let each drop hold a fresh MAX_SNAPSHOT_BYTES. Reconciles on add/remove/clear.
  const snapshotBytesHeldRef = useRef(0);
  // Snapshots taken but not yet visible in a committed `files`, counted on
  // top of the list so the budget is not undercounted while a background run
  // is landing. See utils/awaiting-snapshots.ts for when they are released.
  const snapshotsAwaitingCommitRef = useRef(new Map<File, AwaitingSnapshot>());
  // Every file a committed list has shown. A snapshot taken after its photo
  // was already removed (or uploaded) must still be released from the budget.
  const everListedRef = useRef(new WeakSet<File>());
  useEffect(() => {
    files.forEach((f) => everListedRef.current.add(f.file));
    snapshotBytesHeldRef.current =
      heldSnapshotBytes(files) +
      settleAwaitingSnapshots(
        snapshotsAwaitingCommitRef.current,
        files,
        everListedRef.current,
      );
  }, [files]);

  // Snapshot runs, one after another (see handleUpload). Awaitable by the
  // restore flow, which must not drop its stored copy before this finishes.
  const snapshotChainRef = useRef<Promise<void>>(Promise.resolve());
  // Patience with slow reads, spent once per page rather than once per batch:
  // three 20 s waits per 30-photo batch added up to ~15 minutes for a 300-photo
  // iCloud library. A read that succeeds resets it (nextConsecutiveTimeouts).
  const consecutiveTimeoutsRef = useRef(0);
  // Picks handed to an upload. Snapshotting can still be running for them;
  // their bytes must not be saved for reload any more -- the upload (and the
  // server's intents) own them now.
  const uploadingPickIdsRef = useRef(new Set<string>());

  // Reload survival for picks not yet uploaded (see utils/pending-picks.ts).
  // Read through refs inside handleUpload, which is memoised on [translate].
  const pickSession = pickSessionKey(mainOrderId);
  const pickSessionRef = useRef(pickSession);
  pickSessionRef.current = pickSession;
  const pickedCountRef = useRef(0);
  pickedCountRef.current = files.length;
  // The latest list, for the background snapshot run: by the time it has
  // something to say, a photo may already be removed or uploaded.
  const latestFilesRef = useRef(files);
  latestFilesRef.current = files;

  const handleUpload = useCallback(
    async (
      acceptedFiles: File[],
      thumbnails: string[],
      options: {
        /**
         * Picks restored after a reload, aligned with acceptedFiles: they keep
         * their stored id (so landing or removing one updates its stored
         * record) and their copy count.
         */
        restored?: ReadonlyArray<{ id: string; count: number }>;
      } = {},
    ) => {
      // Some in-app browsers (Instagram/Facebook/TikTok) and older iOS hand
      // back picked images with an EMPTY file.type. Accept those too when the
      // filename looks like an image, so valid photos aren't silently rejected.
      const IMAGE_EXT = /\.(jpe?g|png|gif|webp|heic|heif|avif|bmp|tiff?|jfif)$/i;
      const imageFiles = acceptedFiles.filter(
        (file) =>
          file.type.startsWith("image/") ||
          (!file.type && IMAGE_EXT.test(file.name))
      );
      // A fresh pick starts from a clean slate; anything this pick has to say
      // is added below and by its snapshot run. A restore keeps what is there.
      if (!options.restored) setUploadNotes([]);
      if (imageFiles.length !== acceptedFiles.length) {
        addUploadNote({
          kind: "message",
          id: "filterNonImage",
          text: translate("order.filterNonImage"),
        });
      }

      // Add the files immediately so their previews render without waiting on
      // the byte copy below.
      const indexOf = new Map(acceptedFiles.map((file, index) => [file, index]));
      const restoredOf = (file: File) => options.restored?.[indexOf.get(file) ?? -1];
      const added: FileWithPreview[] = imageFiles.map((file) => ({
        file,
        pickId: restoredOf(file)?.id ?? newPickId(),
        preview: URL.createObjectURL(file),
        thumbnail: thumbnails[indexOf.get(file) ?? -1],
        count: restoredOf(file)?.count ?? 1,
        isHeic:
          file.type === "image/heic" ||
          file.type === "image/heif" ||
          /\.(heic|heif)$/i.test(file.name),
      }));
      setFiles((prevFiles) => [...prevFiles, ...added]);
      if (options.restored) {
        added.forEach((a) => a.pickId && restoredPickIdsRef.current.add(a.pickId));
      }

      if (!isSnapshotEnabled()) return;

      // Not awaited: the picker re-enables the upload button as soon as the
      // thumbnails are in, and the byte copy carries on behind it. An upload
      // started meanwhile simply uses the live handle for photos not copied
      // yet, which is exactly the fallback over the budget anyway. Runs are
      // chained so only one copies at a time and the budget stays accurate.
      const run = snapshotChainRef.current.then(() => snapshotAdded(added));
      snapshotChainRef.current = run.catch(() => {});
    },
    [translate, addUploadNote]
  );

  const snapshotAdded = useCallback(
    async (added: FileWithPreview[]) => {

      // Copy each file's bytes into memory NOW, while the picked handle is still
      // valid, so the upload survives an in-app WebView (Facebook/Instagram)
      // reclaiming the handle before the user reaches checkout. Sequential so
      // the running budget total stays accurate. Seed from the global held total
      // so many separate drops share one MAX_SNAPSHOT_BYTES ceiling. A file that
      // is already unreadable at pick time is dropped here (it would otherwise
      // fail the whole batch at upload) and reported; the rest still upload.
      let held = snapshotBytesHeldRef.current;
      const droppedNames: string[] = [];
      // Restored picks whose stored copy could not be read: picked again, as
      // the pick offer already asks for the ones it could not keep.
      const repickNames: string[] = [];
      // Collected and applied in ONE setFiles below. Updating per file was N
      // re-renders of an N-photo list -- quadratic, and on 1,000 photos the
      // grid re-rendered a thousand times while the customer waited.
      const snapshots = new Map<File, File>();
      const dropped = new Set<File>();
      const stalledNames: string[] = [];
      for (const { file, pickId } of added) {
        // over cap → keep live handle
        if (!withinSnapshotBudget(held, file.size)) continue;

        /*
         * Once a few photos in a row have timed out, stop being patient — but
         * keep looking. This loop is sequential, so staying patient would cost
         * a cloud-backed library 20s per photo. Skipping the rest outright is
         * wrong too: a photo that IS on the device copies in milliseconds, and
         * the snapshot is what protects it from an in-app WebView reclaiming
         * its handle before checkout. So the remaining photos get a short probe.
         */
        const outcome = await snapshotFileBytesResult(
          file,
          snapshotTimeoutFor(consecutiveTimeoutsRef.current),
        );

        consecutiveTimeoutsRef.current = nextConsecutiveTimeouts(
          consecutiveTimeoutsRef.current,
          outcome.status,
        );

        if (outcome.status === "timeout") {
          /*
           * The bytes did not arrive in time. Almost always iCloud: with
           * "Optimise iPhone Storage" the photo lives in the cloud and only its
           * metadata is on the phone, so reading it makes iOS download it
           * first — which can take a long time or stall completely.
           *
           * This is NOT a dead file, so it is not dropped: the live handle is
           * kept and the upload can still fetch it. What must not happen is
           * what used to: the read had no timeout, so a single undownloaded
           * photo froze this sequential loop with no error and no telemetry,
           * and the customer watched thumbnails appear and nothing else happen
           * for as long as they were willing to wait.
           */
          Sentry.captureMessage("Picked file bytes timed out (kept live handle)", {
            level: "warning",
            // Device-aware: the first real event came from an Android phone in
            // the Facebook browser, where "icloud-not-downloaded" is impossible
            // and sends triage after a cause that cannot exist.
            tags: { area: "photo-upload", likelyCause: stallCauseTag() },
            extra: { name: file.name, size: file.size, type: file.type },
          });
          stalledNames.push(file.name);
          continue;
        }

        if (outcome.status === "unreadable") {
          Sentry.captureMessage("Picked file unreadable at add-time (dropped)", {
            level: "warning",
            tags: { area: "photo-upload" },
            extra: { name: file.name, size: file.size, type: file.type },
          });
          if (pickId && restoredPickIdsRef.current.has(pickId)) repickNames.push(file.name);
          else droppedNames.push(file.name);
          dropped.add(file);
          continue;
        }

        const snap = outcome.file;
        held += snap.size;
        snapshots.set(file, snap);
        snapshotsAwaitingCommitRef.current.set(file, {
          size: snap.size,
          settled: false,
          seen: latestFilesRef.current.some((f) => f.file === file),
        });
      }

      if (snapshots.size > 0 || dropped.size > 0) {
        setFiles((prev) =>
          prev
            .filter((f) => !dropped.has(f.file))
            .map((f) => {
              const snap = snapshots.get(f.file);
              return snap ? { ...f, bytes: snap } : f;
            }),
        );
      }
      // This run's setFiles is queued: its entries may now be released once
      // their photos are gone (removed or uploaded meanwhile). Recount from
      // the latest list, since the next run can start before React re-renders.
      snapshots.forEach((_, file) => {
        const entry = snapshotsAwaitingCommitRef.current.get(file);
        if (entry) entry.settled = true;
      });
      snapshotBytesHeldRef.current =
        heldSnapshotBytes(latestFilesRef.current) +
        settleAwaitingSnapshots(
          snapshotsAwaitingCommitRef.current,
          latestFilesRef.current,
          everListedRef.current,
        );

      // Snapshotted bytes are the ones worth keeping across a reload: they are
      // already in memory, and the 80 MB budget bounds what this writes.
      // Fire-and-forget; storage trouble never reaches the customer.
      // With each photo's copy count as it is now, not as it was when picked:
      // the customer may have changed it while the bytes were copied.
      const pickIdOf = new Map(added.map((a) => [a.file, a.pickId as string]));
      const countOf = new Map(
        latestFilesRef.current.map((f) => [f.file, f.count || 1]),
      );
      const addedCountOf = new Map(added.map((a) => [a.file, a.count || 1]));
      const toKeep = [...snapshots]
        .map(([file, snap]) => ({
          id: pickIdOf.get(file) as string,
          file: snap,
          count: countOf.get(file) ?? addedCountOf.get(file) ?? 1,
        }))
        .filter((p) => !uploadingPickIdsRef.current.has(p.id));
      if (toKeep.length > 0) {
        void pendingPicks().save(
          pickSessionRef.current,
          toKeep,
          pickedCountRef.current,
        );
      }

      // The dropped files were already rendered as previews a moment ago, so
      // removing them silently looks like the app losing photos at random --
      // and the customer would only find out at checkout, if at all. Named in
      // a calm inline note (not an alert), so they can pick them again.
      if (droppedNames.length > 0) {
        addUploadNote({ kind: "unreadable", names: droppedNames });
      }
      if (repickNames.length > 0) {
        addUploadNote({ kind: "repick", names: repickNames });
      }

      /*
       * Photos still coming down from iCloud. They are kept and may well
       * upload, but the customer has just watched the page sit there, so say
       * what is happening and what they can do about it. Silence is what made
       * this unreportable: two customers spent days on it and one produced no
       * telemetry at all. The note picks iOS or neutral advice by device
       * (stallAdviceKey, FOTO-NEXTJS-16H).
       */
      // Snapshots run in the background now: a photo the probe already removed,
      // or one already uploaded, must not be named in a "slow photo" note.
      const stillListed = new Set(latestFilesRef.current.map((f) => f.file.name));
      const stalledStillListed = stalledNames.filter((name) => stillListed.has(name));
      if (stalledStillListed.length > 0) {
        addUploadNote({ kind: "stalled", names: stalledStillListed });
      }
    },
    [addUploadNote]
  );

  // Keep the reload store in step with the selection: a removed photo, or a
  // finished upload (which empties the list), drops what this load saved.
  // Debounced, because +/- taps and big picks change `files` in bursts.
  useEffect(() => {
    const timer = setTimeout(() => {
      const selection = new Set<string>();
      const counts = new Map<string, number>();
      files.forEach((f) => {
        if (!f.pickId) return;
        selection.add(f.pickId);
        counts.set(f.pickId, f.count || 1);
      });
      void pendingPicks().reconcile(selection, files.length, counts);
    }, 1000);
    return () => clearTimeout(timer);
  }, [files]);

  // After a reload, offer back what an earlier load had picked for this
  // session. Asked once per session key: the order id often resolves a moment
  // after mount, and a new customer's picks live under "new".
  const [pickOffer, setPickOffer] = useState<RestorablePicks | null>(null);
  const [restoringPicks, setRestoringPicks] = useState(false);
  const askedPickSessionsRef = useRef(new Set<string>());
  useEffect(() => {
    if (askedPickSessionsRef.current.size === 0) void pendingPicks().prune();
    if (askedPickSessionsRef.current.has(pickSession)) return;
    askedPickSessionsRef.current.add(pickSession);
    let cancelled = false;
    void pendingPicks()
      .load(pickSession)
      .then((offer) => {
        if (!cancelled && offer) setPickOffer(offer);
      });
    return () => {
      cancelled = true;
    };
  }, [pickSession]);

  // The guest token for the order an offer belongs to, as a pair from one
  // store (see readOrderSessionStores); a signed-in customer's bearer is
  // added by the axios instance.
  const guestTokenForOrder = (orderId: string): string | null => {
    const session = resolveOrderSession(readOrderSessionStores());
    if (session?.orderId === orderId) return session.guestToken ?? null;
    return mainOrderId === orderId ? (guestToken ?? null) : null;
  };

  const resumePickOffer = async () => {
    // The offer carries the session it was loaded for: the key can change
    // while it is on screen (the order id resolving), and adopting from or
    // discarding the current key instead would leave the offered picks behind.
    const offer = pickOffer;
    if (!offer) return;
    setRestoringPicks(true);
    try {
      // The earlier load is gone (the offer is gated on its Web Lock): give
      // up on the uploads it left waiting before these are sent again under
      // new keys, or Pay would wait for them forever. Bounded and never
      // throws; see abandon-earlier-uploads.ts.
      await abandonForOffer(offer, guestTokenForOrder);
      const restored = offer.picks.map((p) => p.file);
      const thumbnails: string[] = [];
      for (const file of restored) {
        thumbnails.push(await createThumbnail(file, file.type));
      }
      // Restored with their stored ids and copy counts. Snapshotting goes
      // through the normal 80 MB budget: what does not fit stays backed by
      // its stored record, which is why adopt() below moves those records to
      // this load (deleting nothing restored) instead of discarding them.
      await handleUpload(restored, thumbnails, {
        restored: offer.picks.map((p) => ({ id: p.id, count: p.count })),
      });
      // The photos are on screen: the offer has done its job.
      setPickOffer(null);
      // Bytes first, records second. A restored File reads through its
      // stored record, and adopt() rewrites that record; WebKit can release
      // the old record's blob once it is rewritten, after which reading the
      // File fails (NotFoundError) and the photo used to be dropped with an
      // "unreadable" alert. Copy what fits the budget into memory before
      // adopt touches anything.
      await snapshotChainRef.current;
      await pendingPicks().adopt(
        offer.session,
        pickSessionRef.current,
        offer.picks.map((p) => ({ id: p.id, count: p.count })),
        pickedCountRef.current + offer.picks.length,
      );
      // If adopt failed, the earlier records stay: the next load offers them
      // again, which beats deleting the only copy of a restored photo.

      // "Nadaljujte s fotografijami" means "carry on uploading": start it now,
      // through the same path as "Potrdi in naloži", at the size they were
      // picked for. The effect below fires it once the size is applied. With
      // no known size the photos simply stay selected, as before.
      const size = restoredPhotoSize(offer.target, photoSizeOptionsRef.current);
      if (size) {
        setValue("photoSizeId", size);
        setPendingResumeUpload({
          pickIds: new Set(offer.picks.map((p) => p.id)),
          photoSizeId: size,
          target: offer.target,
        });
      }
    } catch (error) {
      Sentry.captureException(error, {
        level: "warning",
        tags: { area: "photo-upload", stage: "restore-picks" },
      });
    } finally {
      setRestoringPicks(false);
      setPickOffer(null);
    }
  };

  // A resume waiting for its size to be applied before it uploads (see above).
  const [pendingResumeUpload, setPendingResumeUpload] =
    useState<PendingResumeUpload | null>(null);
  useEffect(() => {
    if (!pendingResumeUpload) return;
    const decision = resumeUploadDecision({
      pending: pendingResumeUpload,
      files,
      photoSizeId,
      busy: disableSubmit || isUploading,
    });
    if (decision === "wait") {
      // The size normally lands in the same render. If it never does (the
      // customer chose another one first), leave the photos selected.
      const timer = setTimeout(() => setPendingResumeUpload(null), 3000);
      return () => clearTimeout(timer);
    }
    setPendingResumeUpload(null);
    if (decision === "start") {
      void handleCreateOrderAndUpload({ target: pendingResumeUpload.target });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingResumeUpload, files, photoSizeId, disableSubmit, isUploading]);

  const discardPickOffer = () => {
    if (pickOffer) {
      // Same as resuming: those uploads will never come now. Not awaited;
      // it is bounded, never throws, and settles long before Pay.
      void abandonForOffer(pickOffer, guestTokenForOrder);
      void pendingPicks().discardEarlier(pickOffer.session);
    }
    setPickOffer(null);
  };

  // Handle photo reordering
  const handleReorder = (
    dragIndex: number,
    directionOrHoverIndex: number | "up" | "down"
  ) => {
    setFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      let hoverIndex = dragIndex;

      if (typeof directionOrHoverIndex === "number") {
        hoverIndex = directionOrHoverIndex;
      } else if (directionOrHoverIndex === "up" && dragIndex > 0) {
        hoverIndex = dragIndex - 1;
      } else if (
        directionOrHoverIndex === "down" &&
        dragIndex < prevFiles.length - 1
      ) {
        hoverIndex = dragIndex + 1;
      }

      const [draggedPhoto] = newFiles.splice(dragIndex, 1);
      newFiles.splice(hoverIndex, 0, draggedPhoto);
      return newFiles;
    });
  };

  // Helper function to get URL parameters
  function getUrlParameter(name: string) {
    if (typeof window === "undefined") return "";

    name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
    const regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
    const results = regex.exec(window.location.search);
    return results === null
      ? ""
      : decodeURIComponent(results[1].replace(/\+/g, " "));
  }
  // Reset old orders, initialize state and check for URL params
  useEffect(() => {
    // Check for existing order in state
    //
    // Not on in-session paket navigation (a slot row or "Dodaj v paket" adds
    // `bundle`): the order in the cache is the cart the customer is filling
    // right now, not one left behind, and a client-side visit finds it
    // already loaded where a fresh page load would not.
    if (
      order &&
      !searchParams?.get("bundle") &&
      FAILED_BUT_OK_TO_RESUME_STATUSES.includes(order?.status as OrderStatus) &&
      order?.orderPhotos?.length > 0 &&
      order?.amount > 0
    ) {
      setOldOrder(order);
    } // Check for URL parameters that might have order info
    const urlOrderId = getUrlParameter("orderId");
    // Either spelling, or the token the head guard moved out of this URL so
    // analytics could not read it (guest-token-url-guard.ts).
    const urlGuestToken = guestTokenFromUrl(urlOrderId) || "";

    // If no URL params, try to get from localStorage
    let orderIdToUse = urlOrderId;
    let guestTokenToUse = urlGuestToken;
    // Which store this restore ended up taking the pair from. Diagnostics only:
    // when a cart is dropped, the surviving store names the layer that still had
    // a whole session and, by elimination, the ones that had already lost it.
    // Either the URL supplied both halves or the localStorage branch below
    // overwrote both — the two are never mixed.
    let restoredFrom: "url" | "storage" = "url";

    if (!orderIdToUse || !guestTokenToUse) {
      try {
        // Get order data from localStorage with expiration support
        const savedData = getOrderData();

        if (savedData) {
          // Check if we've already failed to authenticate this order
          const attemptKey = `${savedData.orderId}-${savedData.guestToken}`;
          if (failedAuthAttempts.has(attemptKey)) {
            console.log(
              "Skipping localStorage restore - already failed authentication for:",
              attemptKey
            );
            return;
          }

          console.log(
            "Found valid saved order in localStorage:",
            savedData.orderId
          );
          orderIdToUse = savedData.orderId;
          guestTokenToUse = savedData.guestToken;
          restoredFrom = "storage";

          // Update URL with the restored values
          const params = new URLSearchParams(searchParams?.toString() || "");
          params.set("orderId", savedData.orderId);
          params.set("guest_session_token", savedData.guestToken);
          router.replace(`${pathname || ""}?${params.toString()}`);
        }
      } catch (e) {
        console.error("Error reading from localStorage", e);
      }
    }

    if (orderIdToUse && guestTokenToUse) {
      // Create a unique key for this order attempt
      const attemptKey = `${orderIdToUse}-${guestTokenToUse}`;

      // Check if we've already failed to authenticate this order
      if (failedAuthAttempts.has(attemptKey)) {
        console.log(
          "Skipping order fetch - already failed authentication for:",
          attemptKey
        );
        return;
      }

      console.log("Found order info:", orderIdToUse);
      setSelectedOrderId(orderIdToUse);
      setSelectedGuestToken(guestTokenToUse);
      setGuestTokenCookies(guestTokenToUse);

      // Initialize the product context with order info
      setOrderInfo(orderIdToUse, guestTokenToUse);
      console.log("Setting order info in context:");

      // The ONE place this restore throws a session away. Both the success and
      // the failure branch below funnel through it, so a cart can only ever be
      // dropped for a reason order-freshness.ts recognises -- and when it is
      // dropped, it is dropped from every store at once. Leaving one store
      // populated is not a smaller mistake: the readers fall back through URL ->
      // cookie -> localStorage, so a single survivor resurrects the dead
      // session on the next load, and a half-session is what the order-loss
      // diagnostics flag as an actual customer-facing loss (FOTO-NEXTJS-149).
      const dropRestoredSession = (
        reason: CartDropReason,
        details: {
          status?: string | null;
          ageDays?: number | null;
          httpStatus?: number;
        } = {}
      ) => {
        // Reported first, while there is still something to name. The clear
        // below empties every store, so afterwards there is no evidence a cart
        // was ever here -- which is exactly why this class of bug ran for weeks
        // with zero Sentry events. Observation only: nothing below reads it.
        reportCartDropped({
          reason,
          orderId: orderIdToUse,
          orderStatus: details.status ?? null,
          ageDays: details.ageDays ?? null,
          restoredFrom,
          origin: "home-restore",
          httpStatus: details.httpStatus,
        });
        setFiles([]);
        setSelectedOrderId(null);
        setSelectedGuestToken(null);
        setOrderInfo(null, null); // clears BOTH cookies
        setGuestTokenCookies("");
        clearOrderData(); // localStorage
        // ...and the URL, which the restore above reads FIRST. Shared helper on
        // purpose: it strips the legacy `guestToken` spelling too. Deleting
        // `orderId` + `guestToken` while leaving `guest_session_token` behind
        // was the exact signature of the bug this replaces.
        clearOrderSessionFromUrl();
      };

      // Fetch the order details
      orderApi
        .getOneBaseOrderControllerOrder(orderIdToUse, guestTokenToUse)
        .then((response) => {
          const fetchedOrder = response.data;
          // If we get here, the authentication was successful - remove from failed attempts
          setFailedAuthAttempts((prev) => {
            const newSet = new Set(prev);
            newSet.delete(attemptKey);
            return newSet;
          });

          // If the restored order is already completed (paid, payment-on-delivery,
          // shipped, cancelled, refunded, etc.), don't hydrate it into the cart —
          // clear the persisted session so the user starts fresh.
          //
          // Decided by the SAME shouldDropRestoredCart() the provider restore in
          // contexts/index.tsx uses, not by a second status list here. This page
          // used to test TERMINAL_STATUSES, which is a near-duplicate of
          // FINALIZED_ORDER_STATUSES; two lists meaning almost-the-same thing is
          // how the two restores drifted apart in the first place. See the note
          // on TERMINAL_STATUSES in constants/order.constant.tsx for why that
          // list still exists and still keeps PROMOTIONAL.
          const nowMs = Date.now();
          // Same rule, now naming its branch: shouldDropRestoredCart is exactly
          // `cartDropReason(...) !== null`, and the name is what lets the report
          // separate "a finished purchase, correctly let go" from "an order aged
          // out" -- two things that were indistinguishable in a boolean.
          const dropReason = cartDropReason({
            status: fetchedOrder?.status,
            createdAt: fetchedOrder?.createdAt,
            nowMs,
          });
          if (dropReason) {
            dropRestoredSession(dropReason, {
              status: fetchedOrder?.status,
              ageDays: orderAgeDays(fetchedOrder?.createdAt, nowMs),
            });
            return;
          }

          if (fetchedOrder?.id) {
            queryClient.setQueryData(orderQueryKey(fetchedOrder.id), fetchedOrder);
          }

          // Restore order photos from the fetched order
          if (fetchedOrder.orderPhotos && fetchedOrder.orderPhotos.length > 0) {
            // Set the current order photo ID for potential future uploads
            setCurrentOrderPhotoId(
              fetchedOrder.orderPhotos[fetchedOrder.orderPhotos.length - 1]?.id
            );

            // Restore each photo group to the cart
            fetchedOrder.orderPhotos.forEach((orderPhoto) => {
              if (
                orderPhoto.photoSize &&
                orderPhoto.photos &&
                orderPhoto.photos.length > 0
              ) {
                // Create placeholder file objects since we don't have the actual files anymore
                // but we want to represent them in the cart
                const placeholderFiles: FileWithPreview[] =
                  orderPhoto.photos.map((photoUrl) => ({
                    // We don't have the actual file anymore, just URL
                    file: null as unknown as File,
                    preview: photoUrl.url,
                    thumbnail: photoUrl.url,
                    count: 1,
                  }));

              }
            });
          }
          // Set the photo size in the form if available from first orderPhoto
          // ...unless the link names a valid size (a bundle slot's): restoring
          // the cart lands after the URL's size was applied and would undo it.
          // Read through a ref: this effect's closure is from the first render.
          const restoredPhotoSizeId = cartRestorePhotoSizeId(
            getUrlParameter("photoSizeId"),
            photoSizeOptionsRef.current,
            fetchedOrder.orderPhotos?.[0]?.photoSize?.id
          );
          if (restoredPhotoSizeId) {
            setValue("photoSizeId", restoredPhotoSizeId);
          }
          // Load existing products from orderProductDetails
          if (
            fetchedOrder.orderProductDetails &&
            fetchedOrder.orderProductDetails.length > 0
          ) {
            fetchedOrder.orderProductDetails.forEach((orderProductDetail) => {
              if (
                orderProductDetail.product &&
                orderProductDetail.quantity &&
                orderProductDetail.id
              ) {
                const productId = orderProductDetail.product.id;
                const productName = orderProductDetail.product.name;
                const productPrice = orderProductDetail.product.price;

                // Update product context with existing product data
                if (
                  productId &&
                  productName &&
                  typeof orderProductDetail.quantity === "number"
                ) {
                  // ProductContext has been removed - product updates are now handled via API
                  // updateQuantity(productId, orderProductDetail.quantity);
                  // updateName(productId, productName);
                  // updatePrice(productId, productPrice);
                }
              }
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching order from URL params:", error);

          // A failed fetch is NOT a verdict on the order.
          //
          // This branch used to clear cookies, localStorage and the URL on ANY
          // rejection -- a dropped connection, a CORS rejection, a timeout, a
          // 502 while the API restarted -- and a customer with photos already in
          // their cart lost them to a hiccup they never saw. It is the worst
          // outcome in this file, and it was the default one.
          //
          // Only a definitive "gone" (404 / 401 / 403) clears anything now; see
          // shouldDropRestoredCartOnFetchError. Everything else keeps the
          // session exactly as restored above and lets the next real action
          // re-validate it: resuming an order that turns out to be dead is
          // cheap and self-correcting (ensureOrderExists re-checks status before
          // adding, and checkout blocks a non-payable order), whereas a wiped
          // cart is gone for good.
          const definitivelyGone = shouldDropRestoredCartOnFetchError(error);

          if (!definitivelyGone) {
            // Deliberately do NOT record a failed auth attempt: the token was
            // never rejected, so blacklisting the pair would make this and every
            // later restore skip a perfectly good session.
            Sentry.captureException(error);
            return;
          }

          // Record this failed authentication attempt
          const attemptKey = `${orderIdToUse}-${guestTokenToUse}`;
          setFailedAuthAttempts((prev) => {
            const newSet = new Set(prev);
            newSet.add(attemptKey);
            return newSet;
          });

          // Log authentication errors
          if (
            error?.response?.status === 401 ||
            error?.response?.status === 403
          ) {
            console.error(
              translate("Invalid order id or guest token"),
              translate("Please try again or create a new order.")
            );
          }

          // The order is genuinely gone or not ours. Reset every store at once
          // -- a half session (one store cleared, another left behind) is what
          // the order-loss diagnostics flag as a real loss (FOTO-NEXTJS-149),
          // because the next load restores the surviving half and builds a new
          // order on top of this one.
          dropRestoredSession("definitive-404-401-403", {
            httpStatus: error?.response?.status,
          });

          // Only capture exception for actual server errors, not 401/403
          if (
            error?.response?.status !== 401 &&
            error?.response?.status !== 403
          ) {
            Sentry.captureException(error);
          }
        });
    } else {
      // If no URL params, reset the order state
      setFiles((prevFiles) => {
        return [];
      });
      setCoupon(null);
    }

    // Cleanup function to clear failed authentication attempts on unmount
    return () => {
      setFailedAuthAttempts(new Set());
    };
  }, []);

  // Listen for beforeunload event to warn user if they're leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Check if there's an active order that hasn't been completed
      if (mainOrderId && files.length > 0) {
        // Standard way to show a confirmation dialog before leaving
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [mainOrderId, files]);

  // Update estimated time remaining
  useEffect(() => {
    if (uploadProgress > 0 && startTime) {
      const elapsedTime = Date.now() - startTime;
      const estimatedTotalTime = elapsedTime / (uploadProgress / 100);
      const remainingTime = estimatedTotalTime - elapsedTime;
      setEstimatedTimeRemaining(Math.round(remainingTime / 1000)); // Convert to seconds
    }
  }, [uploadProgress, startTime]);

  // Decide whether this upload is long enough to be worth showing the prefill
  // panel. Latch true on the first estimate above the threshold so the panel
  // doesn't disappear as the remaining time counts down near the end.
  useEffect(() => {
    if (estimatedTimeRemaining > PREFILL_MIN_UPLOAD_SECONDS) setPrefillEligible(true);
  }, [estimatedTimeRemaining]);
  // Reset when the upload ends so each new upload re-evaluates from scratch.
  useEffect(() => {
    if (!(uploading || originalUploading)) setPrefillEligible(false);
  }, [uploading, originalUploading]);
  // Handle continuing to checkout
  const handleContinueToCheckout = async () => {
    setCheckoutLoading(true);
    // Resolve the cart as a PAIR from one store: state, then URL, then cookie,
    // then the localStorage backup.
    //
    // The old chains walked each half down its own list and combined whatever
    // each turned up first, so checkout could send order A's id with order B's
    // token -- two different sessions -- and get a 401 back. Worse, it could
    // resolve an id for an order the customer was no longer working on and send
    // them to pay for that one while the batch they had just uploaded sat in a
    // different, abandoned order. Falling back a WHOLE session at a time is the
    // only way the id and the token are guaranteed to describe each other.
    let checkoutSession = null;
    let checkoutStores: OrderSessionStores = {};
    try {
      checkoutStores = readOrderSessionStores();
      checkoutSession = resolveOrderSession(checkoutStores);
    } catch (e) {
      console.error("Error reading the stored order session", e);
    }
    // No complete pair does NOT mean "no cart". A signed-in customer's order
    // carries no guest token at all, and the guard below deliberately treats a
    // missing token as recoverable rather than wiping the cart. So fall back to
    // the bare order id and send NO token — never a token from another session.
    const orderIdToUse =
      checkoutSession?.orderId ?? resolveOrderId(checkoutStores) ?? null;
    const guestTokenToUse = checkoutSession?.guestToken ?? undefined;

    console.log(
      "Using Order ID:",
      orderIdToUse,
      "Guest Token:",
      guestTokenToUse
    );

    // if (!order) {
    //   alert(translate("order.noOrderCreated"));
    //   return;
    // }
    // if (subtotal < minimumOrder) {
    //   if (
    //     coupon &&
    //     couponValid &&
    //     coupon.type === CouponType.PERCENTAGE &&
    //     coupon.discount === 100
    //   ) {
    //     console.log("Coupon is 100% discount");
    //   } else {
    //     alert(translate("currentPrice.minimumOrder", { minimumOrder }));
    //     return;
    //   }
    // }

    // Self-heal: checkout must never dead-end on a stale cart. Reset the dead
    // session and tell the customer -- but do NOT provision a replacement order.
    // See utils/stale-cart-recovery.ts for why.
    const recoverStaleCart = async () => {
      resetStaleCartSession({
        clearOrderData,
        setOrderInfo,
        setGuestTokenCookies,
        setSelectedOrderId,
        setSelectedGuestToken,
        onRecovered: () => {
          setDisableSubmit(false);
          setCheckoutLoading(false);
          // Inline on the page, not an alert: close the drawer so it is seen.
          closeCartDrawer();
          addUploadNote(
            {
              kind: "message",
              id: "cartSessionExpired",
              text: translate(
                "order.cartSessionExpired",
                "Your previous cart session expired and has been reset. Please re-add your items."
              ),
            },
            { reveal: true },
          );
        },
      });
    };

    // Only start a brand-new order when we genuinely cannot identify one. A
    // missing guest TOKEN is NOT a reason to wipe the cart: the order id is
    // still known, the order may belong to a logged-in user (JWT auth, no guest
    // token needed), or the cookie was transiently evicted mid-session on
    // mobile. Destroying here was force-creating a new order on top of one that
    // still held the customer's photos (Sentry: creationReason=force-new,
    // displacedPhotos=yes). If a token is genuinely required and missing, the
    // server check below returns 401/403 and we ask the customer to reload
    // (which re-reads the token) rather than throwing their cart away.
    if (!orderIdToUse) {
      await recoverStaleCart();
      return;
    }

    try {
      setDisableSubmit(true);

      // ProductContext has been removed - products are now managed through ProductStore component
      // Product updates are handled via API through the ProductStore component
      // ...existing code...
      console.log("Fetching updated order data for ID:", orderIdToUse);
      const dataData = await orderApi.getOneBaseOrderControllerOrder(
        orderIdToUse,
        guestTokenToUse
      );
      const orderReceived = dataData.data;

      // Validate the stored order is actually usable. A finalized order
      // (paid / COD placed / shipped / canceled / refunded / ...) can no longer
      // act as the cart, so reset it and start fresh instead of checking it out.
      const orderStatus = (orderReceived as { status?: OrderStatus })?.status;
      if (orderStatus && TERMINAL_STATUSES.includes(orderStatus)) {
        await recoverStaleCart();
        return;
      }

      // Calculate final amount
      const responseAmount = await orderApi.orderControllerCalculateAmount(
        orderIdToUse
      );
      const orderWithAmount = responseAmount.data as unknown as GetOrderDto;
      if (orderWithAmount?.id) {
        queryClient.setQueryData(orderQueryKey(orderWithAmount.id), orderWithAmount);
      }
      setDisableSubmit(false);
      setSelectedOrderId(null);
      setSelectedGuestToken(null);

      // Clear URL parameters before redirecting
      const params = new URLSearchParams(searchParams?.toString() || "");
      params.delete("orderId");
      params.delete("guestToken");

      // Also clean up localStorage
      try {
        // Clear all order data from localStorage
        clearOrderData();
      } catch (e) {
        console.error("Error removing order data from localStorage", e);
      }

      // Redirect to photo options page (additional options come first now)
      const checkoutParams = new URLSearchParams(params.toString());
      // Only carry the token if we actually have one — a logged-in customer
      // checks out via their JWT and may have no guest token at all.
      if (guestTokenToUse) {
        checkoutParams.set("guest_session_token", guestTokenToUse);
      }
      const checkoutUrl = `/order/${orderIdToUse}/photo-options?${checkoutParams.toString()}`;
      router.push(checkoutUrl);
      setCheckoutLoading(false);
    } catch (error: unknown) {
      const err = error as {
        response?: {
          status?: number;
          data?: { message?: string; statusCode?: number };
        };
      };
      const errorMsg = err?.response?.data?.message;
      const errorCode = err?.response?.status ?? err?.response?.data?.statusCode;

      // Only a definitive 404 (the order truly no longer exists) resets the
      // cart. A 401/403 means we could not prove ownership right now — almost
      // always a transient guest-token/cookie loss on mobile — so wiping the
      // cart would throw away a customer's photos for a blip. Keep the cart and
      // ask them to reload, which re-reads the token, instead.
      if (errorCode === 404) {
        await recoverStaleCart();
        return;
      }
      if (errorCode === 401 || errorCode === 403) {
        setDisableSubmit(false);
        setCheckoutLoading(false);
        closeCartDrawer();
        addUploadNote(
          {
            kind: "message",
            id: "checkoutReloadNeeded",
            text: translate(
              "order.checkoutReloadNeeded",
              "Naročila trenutno ni bilo mogoče potrditi. Prosimo, osvežite stran in poskusite znova."
            ),
          },
          { reveal: true },
        );
        return;
      }

      console.error("Checkout error:", errorMsg, "Error " + errorCode);
      console.error("There has been a problem with your operation:", error);
      Sentry.captureException(error);
      setDisableSubmit(false);
      setCheckoutLoading(false);
    }
  };

  // Add product to cart (upsert: backend updates existing line if this
  // productId is already in the order, preventing duplicate rows).
  /**
   * Add a product to the cart, creating the order if there isn't one yet.
   *
   * This used to begin `if (!mainOrderId) return;`, and the prop below was
   * passed as `undefined` unless an order already existed. A customer with an
   * empty cart therefore tapped "add to cart", watched the drawer slide open
   * empty, and nothing happened — no order, no product, no error. Since an
   * order only exists once photos have been uploaded, anyone who wanted to buy
   * a product WITHOUT uploading photos first could not transact at all.
   *
   * `ensureOrderExists` is exactly the remedy for this and always was: it
   * returns the existing order+token pair when there is one and provisions a
   * fresh order when there is not. Its own comment describes starting an order
   * on add-to-cart as the intended path.
   */
  const addProduct = async (productId: string, quantity: number) => {
    try {
      const { orderId, guestToken: token } = await ensureOrderExists();
      await addOrderProduct(orderId, { productId, quantity }, token ?? "");
      // Refresh order data via queryClient
      await queryClient.invalidateQueries({ queryKey: orderQueryKey(orderId) });
    } catch (error) {
      console.error("Failed to add product:", error);
      throw error;
    }
  };

  // Decrementing from the product store. The store knows products by product
  // id while the order knows them by order-product-detail id, and the last step
  // down is a delete rather than a quantity-0 update (the API rejects
  // quantity < 1).
  const setProductQuantity = async (productId: string, quantity: number) => {
    const change = resolveCartQuantityChange(
      order?.orderProductDetails ?? [],
      productId,
      quantity,
    );
    if (change.action === "noop") return;
    try {
      const { orderId, guestToken: token } = await ensureOrderExists();
      if (change.action === "remove") {
        await removeOrderProductDetail(
          orderId,
          { orderProductDetailId: change.orderProductDetailId },
          token ?? "",
        );
      } else {
        await updateOrderProductQuantity(
          orderId,
          {
            orderProductDetailId: change.orderProductDetailId,
            quantity: change.quantity,
          },
          token ?? "",
        );
      }
      await queryClient.invalidateQueries({ queryKey: orderQueryKey(orderId) });
    } catch (error) {
      console.error("Failed to change product quantity:", error);
      throw error;
    }
  };

  // Derive cartQuantities from order's orderProductDetails
  const cartQuantities = React.useMemo(() => {
    const quantities: Record<string, number> = {};
    (order?.orderProductDetails ?? []).forEach((detail) => {
      quantities[
        detail.product?.id || (detail as { productId?: string }).productId || ""
      ] = detail.quantity;
    });
    return quantities;
  }, [order?.orderProductDetails]);

  return (
    <div style={{ padding: "20px 8px 20px" }} ref={parentRef}>
      {rescuedGroups.length > 0 && (
        <RescuedUploadNotice
          groups={rescuedGroups}
          onDismiss={dismissRestoreReconciled}
          onRemoveGroup={handleRemoveRescuedGroup}
          removingGroupId={removingGroupId}
          removeError={removeRescuedError}
        />
      )}
      {pickOffer && (
        <PendingPicksNotice
          saved={pickOffer.picks.length}
          total={pickOffer.total}
          busy={restoringPicks}
          onResume={resumePickOffer}
          onDiscard={discardPickOffer}
        />
      )}
      {oldOrder && (
        <ResumeOldOrder
          title={translate("order.resumeOldOrder")}
          orderId={oldOrder?.id}
          shippingEmail={oldOrder?.shippingEmail}
          paymentOption={oldOrder?.paymentOption as PaymentOption | undefined}
        />
      )}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <Image
          src={logoSrc(currentLocaleFixed, theme.palette.mode)}
          alt="Foto Delavnica"
          width={200}
          height={70}
          priority
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Typography sx={{ mt: "2px" }} variant="h5">
          {translate("order.createNew")}
        </Typography>
        <Tooltip title={translate("order.createNewTooltip")} arrow>
          <IconButton aria-label={translate("order.createNewTooltip")}>
            <InfoIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {/* Photo Size Selector */}
      <PhotoSizeSelector
        control={control}
        errors={errors}
        photoSizeId={photoSizeId}
        photoSize={photoSize}
        autocompleteProps={autocompleteProps}
        setValue={setValue}
        getValues={getValues}
        currentLocaleFixed={currentLocaleFixed}
        currency={currency}
      />
      {/* Credit with a minimum this order does not reach yet: how far to go.
          Nothing for anyone else. */}
      <CreditMinimumProgress />
      {/* Notes about the picked photos: just above the picker, where the
          customer is looking when they appear. */}
      <UploadNotes
        ref={uploadNotesRef}
        notes={uploadNotes}
        inApp={inAppBrowser}
        onDismiss={dismissUploadNote}
      />
      {/* Upload Section */}
      <UploadSection
        files={files}
        disableSubmit={disableSubmit || checkoutLoading}
        onUpload={handleUpload}
        onReorder={handleReorder}
        onCreateOrderAndUpload={() => void handleCreateOrderAndUpload()}
        uploadError={uploadError}
        originalUploadError={originalUploadError}
        uploading={uploading}
        originalUploading={originalUploading}
        uploadProgress={uploadProgress}
        originalUploadProgress={originalUploadProgress}
        uploadingBuffer={uploadingBuffer}
        originalUploadingBuffer={originalUploadingBuffer}
        setDisableSubmit={setDisableSubmit}
        overlaySlot={
          prefillEligible &&
          shouldShowUploadPrefillPanel({
            uploading: uploading || originalUploading,
            orderId: mainOrderId,
            guestToken,
          })
            ? (
              <UploadPrefillPanel
                orderId={mainOrderId as string}
                guestToken={guestToken as string}
                locale={currentLocale}
              />
            )
            : undefined
        }
      />
      {/* Remaining Time and Loading Indicators */}
      {showRemainingTime && (
        <Box>
          <Typography>
            {translate("order.estimatedTimeRemaining")}
            {formatTimeRemaining(estimatedTimeRemaining)}
          </Typography>
        </Box>
      )}
      {isUploading && <ConnectionSpeedComponent />}
      {/* Product Store.
          addProduct is always passed: it provisions the order itself now.
          Gating it on mainOrderId made the button a silent no-op for anyone
          whose cart was still empty. */}
      <ProductStore
        disable={disableSubmit || checkoutLoading}
        addProduct={addProduct}
        setProductQuantity={setProductQuantity}
        cartQuantities={cartQuantities}
      />
      {/* Order Summary */}
      <OrderSummary
        currentPrice={currentPrice}
        goodsCents={currentGoodsCents}
      />
      {/* Spinner overlay when checking out */}
      {checkoutLoading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(255,255,255,0.6)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={64} color="primary" />
          <Typography sx={{ ml: 2 }} variant="h6">
            {translate("order.processingCheckout") || "Processing checkout..."}
          </Typography>
        </Box>
      )}
      {/* Upload Complete Dialog
      <UploadCompleteDialog
        open={showUploadMoreDialog}
        onClose={() => setShowUploadMoreDialog(false)}
        onUploadMore={() => setShowUploadMoreDialog(false)}
        onViewCart={() => {
          setShowUploadMoreDialog(false);
          openCartDrawer();
        }}
      /> */}
      <UnfinishedModal oldOrder={oldOrder} />
    </div>
  );
};

export default NewOrder;
